import React, { useEffect, useState, useCallback } from 'react';
import { getFallbackImage } from '../utils/imageUtils';
import { Card, Button, Dropdown, Alert, Spinner, Container, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from 'react-router-dom';
import { FaWhatsapp, FaEye } from 'react-icons/fa';
import { businessApi } from '../services/api';
import { ROUTES, MESSAGES } from '../constants/Constants';
import { useAuth } from '../context/AuthContext';
import { getImageUrl } from '../constants/Constants';
import { API_BASE_URL } from '../services/api';
import ServiceCategoriesGrid from './ServiceCategoriesGrid';
import BusinessSlider from './BusinessSlider';
import useInfiniteScroll from '../hooks/useInfiniteScroll'; // Import the custom hook

const BusinessList = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [cities, setCities] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');

    // Use the infinite scroll hook
    const { items: businesses, loading, hasMore, error, loadingRef } = useInfiniteScroll(businessApi.fetchPaginated);

    // Effect to extract unique cities and categories from all fetched businesses
    useEffect(() => {
        if (businesses.length > 0) {
            const allCities = new Set();
            const allCategories = new Set();
            businesses.forEach(b => {
                if (b.city) allCities.add(b.city);
                if (b.category) allCategories.add(b.category);
            });
            setCities(Array.from(allCities).sort());
            setCategories(Array.from(allCategories).sort());
        }
    }, [businesses]);

    const handleCityChange = (city) => setSelectedCity(city);
    const handleCategoryChange = (category) => setSelectedCategory(category);

    const filteredBusinesses = businesses.filter(business => {
        const matchesCity = !selectedCity || business.city === selectedCity;
        const matchesCategory = !selectedCategory || business.category === selectedCategory;
        return matchesCity && matchesCategory;
    });

    const handleAddBusiness = () => {
        if (isAuthenticated) {
            navigate(ROUTES.BUSINESS.ADD);
        } else {
            navigate(ROUTES.LOGIN);
        }
    };

    const handleViewBusiness = (businessId) => {
        // 1. Increment the view count
        fetch(`${API_BASE_URL}/increment_view.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ business_id: businessId }),
        })
            .then(res => res.json())
            .then(data => {
                // Optionally, update the view count in state for instant feedback
                // Note: This update will only affect the currently loaded businesses, not future paginated ones.
                // For a full solution, you might need to update the `businesses` state from useInfiniteScroll
                // or refetch the specific business. For now, we'll just navigate.
                navigate(`/business/${businessId}`);
            })
            .catch(() => {
                // On error, still navigate
                navigate(`/business/${businessId}`);
            });
    };

    const handleWhatsAppClick = (business) => {
        const message = `Hi! I'm interested in ${business.name}. Can you provide more information about your services?`;
        const phoneNumber = business.phone ? business.phone.replace(/\D/g, '') : '';

        if (phoneNumber) {
            const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, '_blank');
        } else {
            alert('Phone number not available for this business.');
        }
    };

    if (error) {
        return (
            <Container className="mt-3">
                <Alert variant="danger">
                    {error}
                    <Button variant="link" onClick={() => window.location.reload()} className="p-0 ms-2">
                        Try again
                    </Button>
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Business Directory Home Page</h2>
                <Button variant="primary" onClick={handleAddBusiness}>
                    Add Business
                </Button>
            </div>

            <div className="mb-4">
                <Row>
                    <Col md={6} className="mb-2">
                        <Dropdown>
                            <Dropdown.Toggle variant="outline-secondary" id="city-filter">
                                {selectedCity || 'Filter by City'}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => handleCityChange('')}>
                                    All Cities
                                </Dropdown.Item>
                                {cities.map(city => (
                                    <Dropdown.Item
                                        key={city}
                                        onClick={() => handleCityChange(city)}
                                    >
                                        {city}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Col>
                    <Col md={6} className="mb-2">
                        <Dropdown>
                            <Dropdown.Toggle variant="outline-secondary" id="category-filter">
                                {selectedCategory || 'Filter by Category'}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => handleCategoryChange('')}>
                                    All Categories
                                </Dropdown.Item>
                                {categories.map(category => (
                                    <Dropdown.Item
                                        key={category}
                                        onClick={() => handleCategoryChange(category)}
                                    >
                                        {category}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Col>
                </Row>
            </div>

            {filteredBusinesses.length === 0 && !loading && (
                <Alert variant="info">
                    No businesses found matching the selected filters.
                </Alert>
            )}

            <Row>
                {filteredBusinesses.map(business => (
                    <Col key={business.id} md={6} lg={4} className="mb-4">
                        <Card>
                            <Card.Img
                                variant="top"
                                src={business.profile_photo ? getImageUrl(business.profile_photo) : getFallbackImage('business')}
                                alt={business.name || business.businessName}
                                style={{ height: '200px', objectFit: 'cover' }}
                                onError={(e) => { e.target.onerror = null; e.target.src = getFallbackImage('business'); }}
                            />
                            <Card.Body>
                                <Card.Title>{business.name || business.businessName}</Card.Title>
                                <Card.Text>
                                    {business.description?.slice(0, 100)}
                                    {business.description?.length > 100 ? '...' : ''}
                                </Card.Text>
                                <div className="d-flex justify-content-between align-items-center">
                                    <small className="text-muted">
                                        {business.city} • {business.category}
                                    </small>
                                    <div className="d-flex gap-2">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                className="text-white"
                                                onClick={() => handleViewBusiness(business.id)}
                                            >
                                                View Details
                                            </Button>
                                            <span style={{ fontSize: '0.95em', color: '#555', display: 'flex', alignItems: 'center' }}>
                                                <FaEye style={{ marginRight: 4 }} /> {business.view_count}
                                            </span>
                                        </div>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            className="text-white"
                                            onClick={() => handleWhatsAppClick(business)}
                                            title="Contact via WhatsApp"
                                        >
                                            <FaWhatsapp />
                                        </Button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            {loading && (
                <Container className="d-flex justify-content-center align-items-center my-3">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </Container>
            )}

            {!hasMore && businesses.length > 0 && (
                <Alert variant="info" className="text-center my-3">
                    No more businesses to load.
                </Alert>
            )}

            <div ref={loadingRef} style={{ height: '20px' }}></div> {/* Sentinel for infinite scroll */}

            {/* Slider for verified businesses at the bottom */}
            <div className="mt-5">
                <BusinessSlider />
            </div>
        </Container>
    );
};

export default BusinessList;
